import { proxy, snapshot, useSnapshot } from 'valtio';

import { Item, UpNextMeta } from '#/client/models';
import { devtoolsApi } from '#/dev/devtools';
import { PlaybackOverlayType } from '#/pages/Playback/PlaybackPage.types';

import { resetStore } from '../common';

export interface UpNext {
  recommendations?: Item[] | null;
  meta?: UpNextMeta | null;
}

export interface UpNextStore {
  /** Up next data */
  upNext: UpNext;
  /** Switching visible value triggers show/hide animation for overlay */
  isOverlayVisible: boolean;
  /** showControls is used to show/hide player controls when overlay visibility is switched.
   * We cannot use visible prop alone because we have another variable which actually
   * puts the overlay into the DOM and removes it. */
  showControls: boolean;
  /** eventListeners array holds the event listener callback which are used to track clicking
   * outside of the up next items to be able to dismiss the overlay during playtime.
   * If the player reaches the end we don't want to allow dismissing it. */
  eventListeners: (() => void)[];
  /** If the value is true it means that an up next was triggered so we start the playback
   * from the beginning ignoring the resume point. */
  startFromBeginning: boolean;
  /** ONEWEB-3053 - Deciding whether UpNext is visible is actually more complicated that just the flag above.
   * I had to add some workarounds, which will be soon removed by this ticket. More info there. */
  isOverlayActuallyVisible: boolean;
  overlayType?: PlaybackOverlayType;
}

export const upNextStore = proxy<UpNextStore>({
  upNext: {},
  isOverlayVisible: true,
  showControls: true,
  eventListeners: [],
  startFromBeginning: false,
  isOverlayActuallyVisible: false,
  overlayType: undefined,
});

const initialUpNextStore = snapshot(upNextStore) as UpNextStore;

export const upNextActions = {
  setUpNext(upNext: UpNext) {
    upNextStore.upNext = upNext;
  },
  setOverlayVisibility(visible: boolean) {
    upNextStore.isOverlayVisible = visible;
    upNextStore.showControls = !visible;
    upNextActions.updateActuallyVisible();
  },
  setOverlayType(type: PlaybackOverlayType | undefined) {
    upNextStore.overlayType = type;
    upNextActions.updateActuallyVisible();
  },
  updateActuallyVisible() {
    upNextStore.isOverlayActuallyVisible =
      upNextStore.isOverlayVisible && upNextStore.overlayType !== undefined;
  },
  setShowControls(show: boolean) {
    upNextStore.showControls = show;
  },
  setStartFromBeginning(startFromBeginning: boolean) {
    upNextStore.startFromBeginning = startFromBeginning;
  },
  addEventListener(callback: () => void) {
    upNextStore.eventListeners.push(callback);
  },
  clearEventListeners() {
    while (upNextStore.eventListeners.length) {
      const callback = upNextStore.eventListeners.pop();

      if (callback) {
        document.removeEventListener('click', callback);
      }
    }
  },
  canShowUpNext() {
    return !!upNextStore.upNext.recommendations?.length;
  },
  reset() {
    upNextActions.clearEventListeners();

    resetStore(upNextStore, initialUpNextStore, ['startFromBeginning']);
  },
};

export const useUpNextStore = () => useSnapshot(upNextStore) as UpNextStore;

devtoolsApi.setStore('upNext', upNextStore, upNextActions);
